import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PropertyCard from './PropertyCard';
import PropertyFilters from './PropertyFilters';
import '../Css/PropertySearchPage.css';

const PropertySearchPage = ({ filterType }) => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tiposPropiedad, setTiposPropiedad] = useState([]);

  const [searchParams] = useSearchParams();
  const catParam = searchParams.get('cat'); // ej: "Departamento"

  // Cargar propiedades y tipos
  useEffect(() => {
    const fetchProperties = async () => {
      const { data } = await axios.get('https://backend-admin-nine.vercel.app/api/propiedades');
      return data.filter((p) => p.disponible !== false);
    };

    const fetchTiposPropiedad = async () => {
      const { data } = await axios.get('https://backend-admin-nine.vercel.app/api/types-propiedad');
      return data;
    };

    Promise.all([fetchProperties(), fetchTiposPropiedad()])
      .then(([props, tipos]) => {
        setProperties(props);
        setTiposPropiedad(tipos);
        setFilteredProperties(props);
      })
      .catch((err) => console.error('Error cargando datos:', err))
      .finally(() => setLoading(false));
  }, []);

  // Helper: nombre del tipo para una propiedad (por si no viene populado)
  const getTipoNombre = (prop) => {
    // si ya viene populado
    const direct = prop?.tipoPropiedad?.nombre;
    if (direct) return direct;

    // si viene como id, buscar en tiposPropiedad
    const id = typeof prop?.tipoPropiedad === 'string' ? prop.tipoPropiedad : prop?.tipoPropiedad?._id;
    const match = tiposPropiedad.find((t) => t._id === id);
    return match?.nombre || '';
  };

  // Lista “resuelta” con nombre de tipo para facilitar filtro por ?cat
  const resolvedProps = useMemo(() => {
    if (!properties.length) return [];
    return properties.map((p) => ({ ...p, __tipoNombre: getTipoNombre(p) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, tiposPropiedad]);

  // Reaccionar a filterType (venta/alquiler/…) y ?cat
  useEffect(() => {
    let list = resolvedProps;

    if (filterType) {
      list = list.filter(
        (p) => p.estado?.toLowerCase() === filterType.toLowerCase()
      );
    }

    if (catParam) {
      const wanted = catParam.toLowerCase();
      list = list.filter((p) => p.__tipoNombre?.toLowerCase() === wanted);
    }

    setFilteredProperties(list);
  }, [resolvedProps, filterType, catParam]);

  const handleFilterChange = (filters) => {
    let updated = resolvedProps;

    if (filterType) {
      updated = updated.filter(
        (p) => p.estado?.toLowerCase() === filterType.toLowerCase()
      );
    }

    if (catParam) {
      const wanted = catParam.toLowerCase();
      updated = updated.filter((p) => p.__tipoNombre?.toLowerCase() === wanted);
    }

    if (filters.zona) {
      updated = updated.filter((p) =>
        p.ubicacion?.toLowerCase().includes(filters.zona.toLowerCase())
      );
    }

    if (filters.ambientes) {
      updated = updated.filter((p) =>
        p.caracteristicas?.ambientes === filters.ambientes
      );
    }

    if (filters.dormitorios) {
      updated = updated.filter((p) =>
        p.caracteristicas?.dormitorios === filters.dormitorios
      );
    }

    if (filters.banos) {
      updated = updated.filter((p) =>
        p.caracteristicas?.banos === filters.banos
      );
    }

    if (filters.precioMin) {
      updated = updated.filter((p) => p.precio >= filters.precioMin);
    }

    if (filters.precioMax) {
      updated = updated.filter((p) => p.precio <= filters.precioMax);
    }

    if (filters.moneda) {
      updated = updated.filter((p) => p.moneda === filters.moneda);
    }

    if (filters.caracteristicas) {
      Object.entries(filters.caracteristicas).forEach(([key, value]) => {
        if (value) {
          updated = updated.filter((p) => p.caracteristicas?.[key] === true);
        }
      });
    }

    setFilteredProperties(updated);
  };

  return (
    <div className="property-search-container">
      {loading ? (
        <div className="loading">Cargando propiedades...</div>
      ) : (
        <>
          <div className="filters-container">
            <PropertyFilters onFilterChange={handleFilterChange} />
          </div>

          <div className="properties-container">
            {filteredProperties.length > 0 ? (
              filteredProperties.map((property) => (
                <PropertyCard
                  key={property._id}
                  property={property}
                  tiposPropiedad={tiposPropiedad}
                />
              ))
            ) : (
              <p>No se encontraron propiedades</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PropertySearchPage;
